import { assertNever } from "@p0security/frontend-core";
import { identityTypeToLabel } from "@p0security/shared/assessment/constants";
import { providerLabels } from "@p0security/shared/assessment/render";
import { AppPaths } from "@p0security/shared/routes/constants";
import {
  Identity,
  IdentityType,
  TargetNodeType,
} from "@p0security/shared/types/assessment/data/index";
import { Typography } from "@p0security/ui";
import { ScopeContext } from "components/Assessment/contexts/ScopeContext";
import { useContext } from "react";

import { GraphTooltip } from "../../../GraphTooltip";
import { LinkWithEnvironment } from "../LinkWithEnvironment";
import { identityTypeToEmoji } from "../node/identity/shared";
import { HasAddTerm, ShowHideTerm } from "./ShowHide";

const { Paragraph, Title } = Typography;

/** An identity that be constructed from a consumer or a grant principal */
type CommonIdentity = Omit<Identity, "external">;

const isServiceIdentity = (identityType: IdentityType) =>
  ["service-account", "service-principal", "entra-app-registration"].includes(
    identityType
  );

export const IdentityExternalLink: React.FC<{
  data: Pick<Identity, "isProviderManaged" | "label" | "provider" | "type">;
  id: string;
}> = ({ data }) => <IdentityCell identity={{ ...data }} />;

export const IdentityLink: React.FC<{
  data: CommonIdentity & { id?: string };
  type?: "long" | "short";
  show?: TargetNodeType;
}> = ({ data, show, type }) => {
  const { scopeKey, provider } = useContext(ScopeContext);
  const keyForLink =
    provider === "aws"
      ? (data.id?.split("___")[0] ?? data.label) // TODO: remove this once we have a better way to make the consumers unique
      : (data.id ?? data.label);
  return show === "grant" ? (
    <LinkWithEnvironment
      withTenant={true}
      to={`${AppPaths.Inventory}/identity/${encodeURIComponent(
        keyForLink
      )}?show=identity&where=${encodeURIComponent(
        `identity="${keyForLink}"`
      )}&scope=${encodeURIComponent(scopeKey)}`}
    >
      {type === "short" ? "view" : "View this identity in detail"}
    </LinkWithEnvironment>
  ) : show === "identity" ? (
    <LinkWithEnvironment
      withTenant={true}
      to={`${AppPaths.Inventory}?show=grant&where=${encodeURIComponent(
        `identity:"${keyForLink}"`
      )}&scope=${encodeURIComponent(scopeKey)}`}
    >
      {type === "short" ? "view" : "Explore all entitlements for this identity"}
    </LinkWithEnvironment>
  ) : null;
};

const IdentityInnerDisplay: React.FC<
  Partial<HasAddTerm> & {
    data: CommonIdentity;
    termPrefix?: string;
  }
> = ({ data, onAddTerm, show, terms }) => {
  const { provider } = useContext(ScopeContext);
  const [userName, domain] = data.label.split("@");
  const addProps =
    terms !== undefined && onAddTerm ? { onAddTerm, terms } : undefined;
  return provider ? (
    <div>
      {isServiceIdentity(data.type) ? (
        <>
          <Title level={4}>{userName}</Title>
          {domain && (
            <Paragraph
              /* eslint-disable-line react/forbid-component-props */ style={{
                fontSize: "small",
              }}
            >
              @{domain}
            </Paragraph>
          )}
        </>
      ) : data.type === "public" ? (
        <Title level={4}>Public access</Title>
      ) : (
        <Title level={4}>{data.label}</Title>
      )}
      <Paragraph>
        {identityTypeToEmoji[data.type]}&nbsp;
        {identityTypeToLabel(data)}
        {data.provider ? (
          <>
            &nbsp;in {providerLabels[data.provider]}
            {data.parent ? <>&nbsp;({data.parent})</> : null}
          </>
        ) : null}
      </Paragraph>
      {data.trustedPrincipals?.isConditional && (
        <Paragraph>
          ⚠️ This principal has a conditional trust policy. There may be
          additional constraints on who can access it.
        </Paragraph>
      )}
      {/* This is currently broken when principals come from multiple directories (only a random directory is shown)
       * {data.directory && <Paragraph>{directoryToLabel[data.directory]}</Paragraph>} */}
      {data.isProviderManaged ? (
        provider === "all" ||
        provider === "azure-ad" ||
        provider === "entra-id" ||
        provider === "custom" ||
        provider === "okta" ||
        provider === "workspace" ? (
          <Paragraph>⚠️ This is a service-managed identity.</Paragraph>
        ) : provider === "aws" ? (
          <Paragraph>⚠️ This is a service role.</Paragraph>
        ) : provider === "gcp" ? (
          <Paragraph>
            ⚠️ This is a Google-managed service account. Select &ldquo;Include
            Google-provided role grants&rdquo; in your IAM console to view the
            grant. It is not contained in your project, so access on the project
            does not provide access to this service account.
          </Paragraph>
        ) : provider === "k8s" ? (
          <Paragraph>
            ⚠️ This is a built-in Kubernetes system service account.
          </Paragraph>
        ) : provider === "azure" ? (
          <Paragraph>⚠️ This is a service-managed Azure identity.</Paragraph>
        ) : (
          assertNever(provider)
        )
      ) : null}
      {data.disabled ? (
        provider === "all" ||
        provider === "azure-ad" ||
        provider === "entra-id" ||
        provider === "okta" ||
        provider === "workspace" ||
        provider === "custom" ? (
          <Paragraph>
            ⚠️ This identity is disabled. It cannot be used unless it is first
            enabled.
          </Paragraph>
        ) : provider === "aws" ? (
          <Paragraph>
            ⚠️ This role is disabled via the AWSDenyAll policy. It cannot be
            used unless this policy is removed.
          </Paragraph>
        ) : /* The disabled field is not used in k8s. You cannot disable service accounts. */
        provider === "k8s" ? null : provider === "gcp" ? (
          <Paragraph>
            ⚠️ This is a disabled service account. It cannot be used unless it
            is first enabled.
          </Paragraph>
        ) : provider === "azure" ? (
          <Paragraph>
            ⚠️ This is a disabled Azure identity. It cannot be used unless it is
            first enabled.
          </Paragraph>
        ) : (
          assertNever(provider)
        )
      ) : null}
      <Paragraph>
        <IdentityLink data={data} show={show} />
      </Paragraph>
      {addProps && (
        <>
          <ShowHideTerm
            term={`identity="${data.label}"`}
            name="identities"
            {...addProps}
          />
          <ShowHideTerm
            term={`identity=type:"${
              isServiceIdentity(data.type) && data.isProviderManaged
                ? data.type
                : provider === "aws" && isServiceIdentity(data.type)
                  ? "role"
                  : data.type
            }"`}
            name="identity types"
            {...addProps}
          />
          {isServiceIdentity(data.type) && data.parent && (
            <ShowHideTerm
              term={`identity=parent:"${data.parent}"`}
              name="identity parents"
              {...addProps}
            />
          )}
        </>
      )}
    </div>
  ) : null;
};

export const identityCellValue = (identity: CommonIdentity) =>
  identity.type === "public" ? identityTypeToLabel(identity) : identity.label;

export const IdentityCell = ({
  identity,
  noHover,
  onAddTerm,
  show,
  terms,
  termPrefix,
}: Partial<HasAddTerm> & {
  identity: CommonIdentity;
  noHover?: boolean;
  termPrefix?: string;
}) => {
  const content = (
    <Typography.Text ellipsis>
      {/* TypeScript can't figure out correct typing :( */}
      {identityTypeToEmoji[identity.type]}
      &nbsp;
      {identityCellValue(identity)}
    </Typography.Text>
  );
  return noHover ? (
    content
  ) : (
    <GraphTooltip
      title={
        <IdentityInnerDisplay
          data={identity}
          onAddTerm={onAddTerm}
          show={show}
          terms={terms}
          termPrefix={termPrefix}
        />
      }
      width="400px"
    >
      {content}
    </GraphTooltip>
  );
};
